import { Check, ChevronDown, Store } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type { Restaurant } from '@/api/types'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

/**
 * One Standort, this page only (CHG-2026-09-tax-agency-report).
 *
 * The app-wide LocationSwitcher cannot serve this report for two reasons, both of them decisions
 * rather than omissions:
 *
 * 1. It selects a **list** and treats the empty list as "alle Standorte". A filing is per Standort;
 *    an "alle Standorte" total is a figure nobody files, and the endpoint refuses it.
 * 2. It hides the internal staff store, because that store must appear in no revenue figure of the
 *    manager report. Here it is a normal Standort — the spec says the controller decides how to
 *    treat it — so this control lists every restaurant, INTERNAL ones included.
 *
 * The global store still seeds the selection, so arriving from the dashboard lands on the Standort
 * the reader was already looking at; from then on this page owns its own.
 */
export function AccountingLocationPicker({
  restaurants,
  value,
  onChange,
}: {
  restaurants: Array<Restaurant>
  value: number | null
  onChange: (id: number) => void
}) {
  const { t } = useTranslation('reports')
  const selected = restaurants.find((r) => r.id === value)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-label={t('location.label')}
          className="h-12 w-full justify-start gap-2 font-normal sm:h-9 sm:w-56"
        >
          <Store
            className="h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <span className="truncate">
            {selected?.name ?? t('accounting.pickLocation')}
          </span>
          <ChevronDown
            className="ml-auto h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1">
        {restaurants.map((r) => (
          <button
            key={r.id}
            type="button"
            role="menuitemradio"
            aria-checked={r.id === value}
            onClick={() => onChange(r.id)}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-muted sm:py-1.5"
          >
            <Check
              className={cn('h-4 w-4 shrink-0', r.id !== value && 'invisible')}
              aria-hidden="true"
            />
            <span className="truncate">{r.name}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}
